import { CloseIcon, ExternalLinkIcon } from '@repo/atomic-components';
import { HeaderIconButton } from '@/ui/pages/ChatPage/components/HeaderIconButton';
import { FileTypeIcon } from '@/ui/components/FileTypeIcon';

interface HeaderProps {
  documentName: string;
  onClose: () => void;
  documentPath: string;
  /** Explicit file extension for the leading file-type icon; parsed from `documentName` when omitted. */
  documentFormat?: string | null;
  /** `name` query param for the full document viewer (defaults to `documentName`) */
  viewerNameQueryParam?: string;
  /** File size query param for the full document viewer. */
  viewerSizeMbQueryParam?: number | null;
  showOpenInNewTab?: boolean;
}

export const Header = ({
  documentName = '',
  documentFormat,
  documentPath,
  onClose,
  viewerNameQueryParam,
  viewerSizeMbQueryParam,
  showOpenInNewTab = true,
}: HeaderProps) => {
  const openDocument = () => {
    const url = new URL(documentPath, window.location.origin);
    url.searchParams.set('mode', 'viewer');
    const nameForViewer = viewerNameQueryParam ?? documentName;
    if (nameForViewer) {
      url.searchParams.set('name', nameForViewer);
    }
    if (typeof viewerSizeMbQueryParam === 'number' && Number.isFinite(viewerSizeMbQueryParam)) {
      url.searchParams.set('sizeMb', String(viewerSizeMbQueryParam));
    }
    window.open(url.toString());
  };

  return (
    <div className="border-theme-border-grey bg-theme-white/80 absolute top-0 right-0 left-0 z-30 flex h-14 items-center gap-3 border-b border-l pr-3 pl-4 backdrop-blur-xl backdrop-saturate-[2]">
      <div className="text-theme-black flex min-w-0 flex-1 items-center gap-2">
        <FileTypeIcon fileName={documentName} extension={documentFormat} height={20} className="shrink-0" />
        <span className="truncate text-[0.875rem]" title={documentName}>
          {documentName}
        </span>
      </div>
      <div className="ml-auto flex items-center gap-0.5">
        {showOpenInNewTab && (
          <HeaderIconButton
            icon={<ExternalLinkIcon width={16} height={16} />}
            tooltip="Open in new tab"
            tooltipPosition="left"
            onClick={openDocument}
          />
        )}
        <HeaderIconButton
          icon={<CloseIcon width={12} height={12} />}
          tooltip="Close preview"
          tooltipPosition="left"
          onClick={onClose}
        />
      </div>
    </div>
  );
};
